/**
 * Referral panel (1.2): four blocks and one button.
 *
 *   Header        — who, from where, payer with the auth phrase, zip and
 *                   territory, expected discharge, SOC by day. Nothing else.
 *   Checks        — service area, SOC inside 48h, capacity per ordered
 *                   discipline tightest first, authorization. One summary dot.
 *   Expected Plan — the template in a dropdown with Custom at the bottom, then
 *                   one row per discipline with the pattern in words and the
 *                   points a week it reserves.
 *   SOC Options   — only the clinicians who fit, with the other clinicians and
 *                   the one reason each does not fit behind a disclosure.
 *
 * Then one button, which names what it will hold. No verdict, no headline, no
 * Defer, no decline: a failed check is a reason on the screen and nothing
 * blocks. Accepting re-checks and holds in one step, moves the row to Pending
 * admissions, closes the panel and leaves a one-line receipt on the queue.
 */
import { Fragment, useMemo, useState } from "react";
import { Alert, Box, Button, Collapse, Divider, Link as MuiLink, MenuItem, Paper, Radio, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography } from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useNow } from "../../components/useNow";
import { dateKeyStart, formatInBranchZone, toDateKey } from "@caredna-marlin/utils/homeHealthDemo/engine/clock";
import { CUSTOM_LABEL, CUSTOM_PLAN_KEY, ORDERS_ON_FILE_KEY, ORDERS_ON_FILE_LABEL, hasFrequenciesOnFile, planTemplates, type ExpectedPlan } from "../../engine/expectedPlan";
import {
  DISCIPLINE_GROUP_SHORT,
  acceptButtonLabel,
  acceptReceiptText,
  partOfDayFor,
  selectReferralChecks,
  type CapacityRow,
  type Check,
  type CheckStatus,
  type CheckSummary,
  type ReferralChecks,
  type SocCandidate,
} from "../../engine/referralChecks";
import { describeReserved } from "../../engine/reserved";
import { rankColor, rankDisplayName } from "~/utils/scheduleOptions";
import { acceptReferral, declineReferral, setReferralPlan } from "../../presenter/actions";
import type { FrequencyOrder, Referral } from "@caredna-marlin/utils/homeHealthDemo/state";
import { useDemoState } from "../../store/useDemoStore";
import { useDemoBranchClock } from "../../components/branchClock";
import { useSocScheduleOptions } from "./useSocScheduleOptions";

const CHECK_GLYPH: Record<CheckStatus, { glyph: string; color: string }> = {
  pass: { glyph: "✓", color: "#2e7d32" },
  caution: { glyph: "!", color: "#b26a00" },
  warn: { glyph: "!", color: "#d84315" },
  fail: { glyph: "✕", color: "#c62828" },
  info: { glyph: "i", color: "#607d8b" },
};

export const SUMMARY_COLOR: Record<CheckSummary, { dot: string; border: string; bg: string }> = {
  clear: { dot: "#2e7d32", border: "#a5d6a7", bg: "#f1f8f1" },
  marginal: { dot: "#b26a00", border: "#ffe082", bg: "#fff8e1" },
  blocked: { dot: "#c62828", border: "#ef9a9a", bg: "#fdecea" },
};

/** The payer with the auth phrase folded in, as the header reads it. */
const PAYER_PHRASE = { MA: "Medicare Advantage", FFS: "Medicare FFS, no auth needed" } as const;

export function ReferralPanel({ referralId }: { referralId: string }) {
  const state = useDemoState();
  const navigate = useNavigate();
  const now = useNow();
  const [busy, setBusy] = useState(false);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const onDecline = () => {
    if (!confirmDecline) {
      setConfirmDecline(true);
      return;
    }
    setConfirmDecline(false);
    declineReferral(referralId);
    navigate("/referrals");
  };
  const referral = state.referrals[referralId];
  const patient = referral ? state.patients[referral.patientRef] : undefined;
  const tz = state.config.branch.timeZone;
  /**
   * **The product's scheduler answers "who can take this".** Asked on open, for
   * the referral's own 48-hour window, and handed to the selector as the
   * authoritative answer — including when the answer is none. There is no
   * fallback to the demo's own search: on 2026-09-23 that search said *no SOC
   * slots available* for a patient two nurses could have seen, and a silent
   * second opinion is how that went unnoticed for days.
   */
  const soc = useSocScheduleOptions(state, referralId, toDateKey(now, tz));
  const checks = useMemo(
    () => (referral ? selectReferralChecks(state, referralId, { wallNow: now, candidateSlots: soc.slots, providerEligibility: soc.eligibility, slotsAreAuthoritative: soc.answered, slotsPending: soc.loading }) : undefined),
    [state, referralId, referral, now, soc.slots, soc.eligibility, soc.answered, soc.loading],
  );
  const clock = useDemoBranchClock();
  const templates = useMemo(() => planTemplates(state.config), [state.config]);
  const ordersOnFile = !!referral && hasFrequenciesOnFile(referral);

  if (!referral || !checks || !patient) {
    return (
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Referral not found.
        </Typography>
      </Paper>
    );
  }

  const [chosen, setChosen] = useState<string | undefined>(undefined);
  const hold = Object.values(state.holds).find((h) => h.referralId === referralId);
  const onAccept = async () => {
    setBusy(true);
    try {
      // The option as this screen has it — the scheduler's clinician, day and
      // hour — so the hold lands on what the room was just shown.
      const picked = checks.socOptions.find((c) => c.handle === (chosen ?? checks.socOptions[0]?.handle));
      const placed = await acceptReferral(referralId, picked?.slotDate && picked.slotTime ? { handle: picked.handle, date: picked.slotDate, time: picked.slotTime } : undefined);
      // The row leaves Referrals with its hold; the receipt travels to the queue.
      navigate("/referrals", { state: { receipt: acceptReceiptText(state, referralId, placed) } });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, display: "flex", flexDirection: "column", gap: 2 }}>
      {/* 1. header */}
      <Box>
        <Typography variant="h6">{patient.displayName}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
          {referral.source} · {PAYER_PHRASE[patient.payer]} · {patient.zip} · {checks.territory ?? "no territory"} · expected discharge{" "}
          {formatInBranchZone(dateKeyStart(checks.expectedDischarge, tz), tz, "EEE MMM d")} · SOC by {formatInBranchZone(new Date(checks.socDueByIso), tz, "EEE MMM d")}
        </Typography>
      </Box>

      {/* 2. the checks */}
      <Paper variant="outlined" sx={{ p: 1.5, borderColor: SUMMARY_COLOR[checks.summary].border, bgcolor: SUMMARY_COLOR[checks.summary].bg }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
          <Box aria-label={`checks ${checks.summary}`} sx={{ width: 12, height: 12, borderRadius: "50%", bgcolor: SUMMARY_COLOR[checks.summary].dot }} />
          <Typography variant="subtitle2" fontWeight={600}>
            Checks
          </Typography>
        </Stack>
        <Stack spacing={0.75}>
          {checks.checks.map((c) => (
            <CheckRow key={c.key} check={c} capacity={c.key === "capacity" ? checks.capacity : undefined} />
          ))}
        </Stack>
      </Paper>

      {/* 3. the expected plan: one selector for the tail, the capacity row and the hold */}
      <ExpectedPlanBlock referral={referral} plan={checks.plan} rows={checks.expectedPlan} editable={referral.status === "new"} templates={templates} ordersOnFile={ordersOnFile} />

      {/* 4. who can start this patient */}
      <SocOptions checks={checks} tz={tz} chosen={chosen} onChoose={setChosen} loading={soc.loading} error={soc.error} />

      <Divider />

      {/* one button, which names what it will hold */}
      {referral.status === "new" ? (
        <Stack direction="row" spacing={1} alignItems="center">
          <Button variant="contained" color="primary" disabled={busy} onClick={onAccept}>
            {acceptButtonLabel(state, referralId, now)}
          </Button>
          {/* Asks once before it goes, the way Reset this browser does, because a
              mis-click here takes a referral off the queue mid-act. */}
          <Button variant="text" color={confirmDecline ? "error" : "inherit"} disabled={busy} onClick={onDecline} onBlur={() => setConfirmDecline(false)}>
            {confirmDecline ? "Confirm decline" : "Decline"}
          </Button>
        </Stack>
      ) : referral.status === "accepted" && hold ? (
        <Alert severity="success" variant="outlined">
          Hold placed on {state.roster[hold.providerHandle ?? ""]?.displayName ?? "unassigned"}, {formatInBranchZone(dateKeyStart(hold.date, tz), tz, "EEE MMM d")} {clock.wall(hold.windowStart)}–{clock.wall(hold.windowEnd)} · reserved{" "}
          {hold.tail.map((t) => `~${t.pointsPerWeek} points/wk ${t.discipline}`).join(" + ")}. See{" "}
          <MuiLink component={RouterLink} to="/pending-admissions">
            Pending admissions
          </MuiLink>
          .
        </Alert>
      ) : (
        <Alert severity="info" variant="outlined">
          {referral.status === "released" ? "Released." : referral.status === "declined" ? "Declined. It is off the queue." : "This referral has not arrived yet."}
        </Alert>
      )}
    </Paper>
  );
}

function CheckRow({ check, capacity }: { check: Check; capacity?: CapacityRow[] }) {
  const clock = useDemoBranchClock();
  const g = CHECK_GLYPH[check.status];
  const detail = clock.sentence(check.detail);
  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: g.color, color: "#fff", fontSize: 11, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, mt: "2px" }}>{g.glyph}</Box>
      <Box>
        <Typography variant="body2" component="div">
          <b>{check.label}</b>{" "}
          {capacity ? (
            capacity.length === 0 ? (
              <span>{detail}</span>
            ) : (
              // One sentence, not a stack of lines (Pete, 2026-09-22): the week and
              // the qualifier are said once and the disciplines follow it.
              <span>
                Next week with this patient:{" "}
                {capacity.map((row, i) => (
                  <Fragment key={`${row.territory}-${row.group}`}>
                    {i > 0 && ", "}
                    <CapacityFigure row={row} />
                  </Fragment>
                ))}
              </span>
            )
          ) : (
            detail
          )}
        </Typography>
        {check.note && (
          <Typography variant="caption" color="text.secondary">
            {check.note}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

/**
 * One discipline's figure inside the capacity sentence: next week's load with
 * this referral's expected plan added, against the target. No territory (the
 * Service area row names it once, and every figure here is that same territory)
 * and no figure without this patient — what the number would have been had the
 * branch said no is not a thing the room is weighing (Pete, 2026-09-22). The
 * hover still breaks the load down: planned, then everything reserved by reason,
 * then this referral's own expected plan, which is the only part the dropdown
 * moves.
 */
function CapacityFigure({ row }: { row: CapacityRow }) {
  const group = DISCIPLINE_GROUP_SHORT[row.group] ?? row.group;
  const hover = `planned ${row.planned} · reserved ${row.reserved} (${describeReserved(row.reservedByReason)}) · this referral ${row.thisReferral}`;
  return (
    <Tooltip title={hover}>
      <Box component="span" sx={{ cursor: "help", color: row.over ? "#b26a00" : undefined, fontWeight: row.over ? 600 : undefined }}>
        {group} {row.withThis} of {row.target}
      </Box>
    </Tooltip>
  );
}

/**
 * The expected plan: the template in a dropdown with Custom at the bottom, then
 * one read-only row per discipline. Under Custom the rows become inputs in the
 * same pattern notation, plus the horizon in weeks, and the capacity line above
 * recomputes as they change — one selector, one number everywhere.
 */
function ExpectedPlanBlock({
  referral,
  plan,
  rows,
  editable,
  templates,
  ordersOnFile,
}: {
  referral: Referral;
  plan: ExpectedPlan;
  rows: ReferralChecks["expectedPlan"];
  editable: boolean;
  templates: { key: string; name: string }[];
  ordersOnFile: boolean;
}) {
  const custom = plan.source === "custom";
  const onSelect = (key: string) => {
    if (key === CUSTOM_PLAN_KEY) {
      // Custom starts from whatever is on screen, so nothing jumps when it opens.
      setReferralPlan(referral.id, { kind: "custom", orders: plan.orders, horizonWeeks: plan.horizonWeeks });
      return;
    }
    setReferralPlan(referral.id, key === ORDERS_ON_FILE_KEY ? { kind: "ordersOnFile" } : { kind: "template", templateKey: key });
  };
  const onPattern = (discipline: string, pattern: string) => {
    const orders: FrequencyOrder[] = plan.orders.map((o) => (o.discipline === discipline ? { ...o, pattern } : o));
    setReferralPlan(referral.id, { kind: "custom", orders, horizonWeeks: plan.horizonWeeks });
  };
  const onHorizon = (weeks: number) => {
    if (!Number.isFinite(weeks) || weeks < 1) return;
    setReferralPlan(referral.id, { kind: "custom", orders: plan.orders, horizonWeeks: Math.round(weeks) });
  };

  return (
    <Box>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        <Typography variant="subtitle2" fontWeight={600}>
          Expected plan
        </Typography>
        <TextField select size="small" label="Plan" value={plan.selectionKey} disabled={!editable} onChange={(e) => onSelect(e.target.value)} sx={{ minWidth: 280 }}>
          {ordersOnFile && <MenuItem value={ORDERS_ON_FILE_KEY}>{ORDERS_ON_FILE_LABEL}</MenuItem>}
          {templates.map((t) => (
            <MenuItem key={t.key} value={t.key}>
              {t.name}
            </MenuItem>
          ))}
          <MenuItem value={CUSTOM_PLAN_KEY}>{CUSTOM_LABEL}</MenuItem>
        </TextField>
        {custom && editable && (
          <TextField size="small" type="number" label="Weeks" value={plan.horizonWeeks} onChange={(e) => onHorizon(Number(e.target.value))} inputProps={{ min: 1, max: 12, "aria-label": "Horizon in weeks" }} sx={{ width: 110 }} />
        )}
      </Stack>
      <Table size="small" sx={{ "& td, & th": { py: 0.25 } }}>
        <TableHead>
          <TableRow>
            <TableCell>Discipline</TableCell>
            <TableCell>Pattern</TableCell>
            <TableCell align="right">Points / wk</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={`${r.discipline}-${r.requiredTraits.join("+")}`}>
              <TableCell>
                {r.discipline}
                {r.requiredTraits.length ? ` (${r.requiredTraits.join(", ")})` : ""}
              </TableCell>
              <TableCell>
                {custom && editable ? (
                  <TextField size="small" value={r.pattern} onChange={(e) => onPattern(r.discipline, e.target.value)} inputProps={{ "aria-label": `${r.discipline} pattern` }} sx={{ width: 180 }} />
                ) : (
                  r.patternWords
                )}
              </TableCell>
              <TableCell align="right">{r.pointsPerWeek}</TableCell>
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={3}>
                <Typography variant="body2" color="text.secondary">
                  Nothing to reserve yet.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}

/**
 * SOC Options: only the clinicians who fit — SOC-capable, carrying every trait
 * the case needs, with a slot inside 48 hours of discharge. Below them
 * **Unavailable SOC Clinicians**, a disclosure holding the ones who carry the
 * case's traits and could not take it, one of three readings each: Not working,
 * No capacity, or In orientation, No capacity. A clinician on another
 * discipline, or without a trait the case needs, is not on this screen at all.
 */
function SocOptions({ checks, tz, chosen, onChoose, loading, error }: { checks: ReferralChecks; tz: string; chosen?: string; onChoose?: (handle: string) => void; loading?: boolean; error?: string }) {
  const [open, setOpen] = useState(false);
  const picked = chosen ?? checks.socOptions[0]?.handle;
  const traits = checks.caseTraits.length ? `, carrying ${checks.caseTraits.join(" and ")},` : "";
  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600}>
        SOC Options
      </Typography>
      <Table size="small" sx={{ mt: 0.5, "& td, & th": { py: 0.25 } }}>
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell>Clinician</TableCell>
            <TableCell>Type</TableCell>
            <TableCell>When</TableCell>
            <TableCell align="right">Drive</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Three different things, said differently: still asking, asked and
              could not reach the scheduler, asked and it had nothing. They used
              to be one empty table. */}
          {loading && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  Loading…
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading && error && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="error.main">
                  The scheduler could not be reached, so there is nothing to offer. {error}
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {!loading && !error && checks.socOptions.length === 0 && (
            <TableRow>
              <TableCell colSpan={5}>
                <Typography variant="body2" color="text.secondary">
                  No SOC-capable clinician has a slot inside 48 hours of discharge.
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {checks.socOptions.map((c) => (
            // Pickable, not a read-out (Pete, 2026-09-22): the whole row selects,
            // so the answer the button reserves is the one somebody chose.
            <TableRow key={c.handle} hover selected={c.handle === picked} onClick={() => onChoose?.(c.handle)} sx={{ cursor: onChoose ? "pointer" : undefined }}>
              <TableCell padding="checkbox">
                <Radio size="small" checked={c.handle === picked} onChange={() => onChoose?.(c.handle)} inputProps={{ "aria-label": `Reserve ${c.displayName}` }} />
              </TableCell>
              <TableCell>{c.displayName}</TableCell>
              <TableCell>{c.discipline}</TableCell>
              <TableCell>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <RankDot rank={c.rankCategory} />
                  <span>{whenLabel(c, tz)}</span>
                </Stack>
              </TableCell>
              <TableCell align="right">{c.driveMinutes !== undefined ? `${c.driveMinutes} min` : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {checks.disclosure.length > 0 && (
        <Box sx={{ mt: 0.5 }}>
          <MuiLink component="button" type="button" variant="body2" underline="hover" onClick={() => setOpen((v) => !v)}>
            Unavailable SOC Clinicians
          </MuiLink>
          <Collapse in={open}>
            <Table size="small" sx={{ mt: 0.5, "& td, & th": { py: 0.25 } }}>
              <TableBody>
                {checks.disclosure.map((c) => (
                  <TableRow key={c.handle}>
                    <TableCell sx={{ width: 180 }}>{c.displayName}</TableCell>
                    <TableCell sx={{ width: 60 }}>{c.discipline}</TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {c.exclusion}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}

/**
 * **How good the scheduler thinks this slot is**, in its own three words: a
 * green, amber or red dot beside the day. `rankColor` and `rankDisplayName` are
 * the product's own — the same mapping its Available times list uses — so the
 * panel and the scheduler screen opened beside it agree about what a green
 * option is. They hand back MUI palette keys, not colours, so this follows the
 * theme like anything else.
 *
 * Nothing is drawn for a slot this file found itself: it ranks nothing, and a
 * dot with no opinion behind it would be a colour somebody reads meaning into.
 */
function RankDot({ rank }: { rank?: string }) {
  if (!rank) return null;
  const color = rankColor(rank) ?? "success";
  const name = rankDisplayName(rank);
  return (
    <Tooltip title={name ? `${name} — the scheduler rates this option ${rank}` : ""}>
      <Box component="span" aria-label={`Scheduler rating ${rank}`} sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: `${color}.main`, flexShrink: 0 }} />
    </Tooltip>
  );
}

/** "Thursday morning": the day and the part of day, never a clock time. */
function whenLabel(c: SocCandidate, tz: string): string {
  if (!c.slotDate || !c.slotTime) return "—";
  return `${formatInBranchZone(dateKeyStart(c.slotDate, tz), tz, "EEEE")} ${c.partOfDay ?? partOfDayFor(c.slotTime)}`;
}
